import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslations } from '../../../hooks/use-translations';

export function hasDisplayValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '' && String(value).trim() !== '-';
}

export function joinDisplayValues(values, separator = ' • ') {
  return (values || []).filter(hasDisplayValue).join(separator);
}

export function ReportIdentityCard({ title, lines = [], emptyTitle = '—' }) {
  const visibleLines = (lines || []).filter(hasDisplayValue);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {hasDisplayValue(title) ? title : emptyTitle}
      </Typography>
      {visibleLines.map((line, index) => (
        <Typography key={`${index}-${typeof line === 'string' ? line : 'line'}`} variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

ReportIdentityCard.propTypes = {
  title: PropTypes.node,
  lines: PropTypes.arrayOf(PropTypes.node),
  emptyTitle: PropTypes.string,
};

export function ReportStackedCell({ items = [], layout = 'between', labelWidth = 52 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, minWidth: 0 }}>
      {items.map((item, index) => {
        const row = (
          <Box
            sx={{
              display: 'flex',
              alignItems: layout === 'inline' || (item.value != null && typeof item.value !== 'string' && typeof item.value !== 'number')
                ? 'center'
                : 'baseline',
              justifyContent: layout === 'between' ? 'space-between' : 'flex-start',
              gap: layout === 'inline' ? 0.75 : 1,
              minWidth: 0,
              width: '100%',
            }}
          >
            {hasDisplayValue(item.label) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  ...(layout === 'inline' ? { width: item.labelWidth || labelWidth, flexShrink: 0 } : {}),
                }}
              >
                {item.label}
              </Typography>
            )}
            {item.value != null && item.value !== '' && (
              typeof item.value === 'string' || typeof item.value === 'number' ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 0,
                    ...(item.valueSx || {}),
                  }}
                >
                  {item.value}
                </Typography>
              ) : item.value
            )}
            {item.extra}
          </Box>
        );

        if (!item.hint) {
          return <React.Fragment key={item.key || `${item.label || 'item'}-${index}`}>{row}</React.Fragment>;
        }
        return (
          <Tooltip key={item.key || `${item.label || 'item'}-${index}`} title={item.hint} arrow>
            <Box sx={{ minWidth: 0, width: '100%' }}>{row}</Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

ReportStackedCell.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.node,
    value: PropTypes.node,
    hint: PropTypes.node,
    extra: PropTypes.node,
    labelWidth: PropTypes.number,
    valueSx: PropTypes.object,
  })),
  layout: PropTypes.oneOf(['between', 'inline']),
  labelWidth: PropTypes.number,
};

export function ReportMobileCard({ children }) {
  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      {children}
    </Box>
  );
}

ReportMobileCard.propTypes = {
  children: PropTypes.node,
};

export function ProfileIdentityCell({
  row,
  nameKey = 'profileFullName',
  companyKey = 'contractingCompanyName',
  jobKey = 'jobTitle',
  siteKey = 'siteName',
  extraLines = [],
}) {
  const site = row?.[siteKey] || row?.siteName || row?.site;
  return (
    <ReportIdentityCard
      title={row?.[nameKey]}
      lines={[
        joinDisplayValues([row?.[companyKey], row?.[jobKey]]),
        site,
        ...extraLines,
      ]}
    />
  );
}

ProfileIdentityCell.propTypes = {
  row: PropTypes.object,
  nameKey: PropTypes.string,
  companyKey: PropTypes.string,
  jobKey: PropTypes.string,
  siteKey: PropTypes.string,
  extraLines: PropTypes.arrayOf(PropTypes.node),
};

export function CompanyIdentityCell({
  row,
  nameKey = 'name',
  siteKey = 'siteName',
  peopleKey = 'totalNumberOfPeople',
  extraLines = [],
}) {
  const { t } = useTranslations();
  const people = row?.[peopleKey];
  const peopleLine = people !== null && people !== undefined && String(people).trim() !== ''
    ? t('reports.peopleCount').replace('{count}', people)
    : null;
  return (
    <ReportIdentityCard
      title={row?.[nameKey]}
      lines={[row?.[siteKey], peopleLine, ...extraLines]}
    />
  );
}

CompanyIdentityCell.propTypes = {
  row: PropTypes.object,
  nameKey: PropTypes.string,
  siteKey: PropTypes.string,
  peopleKey: PropTypes.string,
  extraLines: PropTypes.arrayOf(PropTypes.node),
};

export function ReportMetricValue({ children }) {
  return (
    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', py: 0.5 }}>
      {children}
    </Typography>
  );
}

ReportMetricValue.propTypes = {
  children: PropTypes.node,
};

export function renderMobileColumnCells(columns, row) {
  return (columns || [])
    .filter((column) => column && column.hide !== true && column.field !== 'mobileView')
    .map((column) => {
      const params = { row, value: row?.[column.field], field: column.field };
      const content = column.renderCell
        ? column.renderCell(params)
        : (params.value ?? '');

      return (
        <Box key={column.field} sx={{ minWidth: 0 }}>
          {content}
        </Box>
      );
    });
}

export function getMobileGridColumns(columns) {
  return [
    {
      field: 'mobileView',
      headerName: '',
      flex: 1,
      sortable: false,
      filterable: false,
      hideable: false,
      resizable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <ReportMobileCard>
          {renderMobileColumnCells(columns, params.row)}
        </ReportMobileCard>
      ),
    },
  ];
}
